import { formatDate } from 'core/helpers';
import { JsonObject } from 'core/types';
import {
  ClientFileStatus,
  FtsStatus,
  Payment,
  PaymentReportStatus,
  SignatureStatus,
  SourceType,
  Status,
  UploadStatus,
} from '../enums';
import { TransactionGroup } from './TransactionGroup';

export const sourceTypeFormat = (sourceType: string): SourceType => {
  return SourceType[sourceType];
};

export const signatureStatusFormat = (status: SignatureStatus, signableByUser: boolean): Status => {
  switch (status) {
    case SignatureStatus.OPEN:
      return Status.OPEN;
    case SignatureStatus.UNSIGNED:
      return signableByUser ? Status.UNSIGNED : Status.UNSIGNED_NO_PERMISSION;
    case SignatureStatus.PARTIALLY_SIGNED:
      return signableByUser ? Status.PARTIALLY_SIGNED : Status.PARTIALLY_SIGNED_NO_PERMISSION;
    case SignatureStatus.SIGNATURE_RULE_MISSING:
      return Status.MISSING_SIGNATURE_RULE;
    case SignatureStatus.FULLY_SIGNED:
      return Status.FULLY_SIGNED;
    default:
      return null;
  }
};

export const ftsStatusFormat = (status: FtsStatus): Status => {
  switch (status) {
    case FtsStatus.RJCT:
      return Status.ERROR;
    case FtsStatus.PART:
      return Status.WARNING;
    case FtsStatus.ACCP:
      return Status.SUCCESS_COMPLETE;
    default:
      return null;
  }
};

export const dbStatusFormat = (status: PaymentReportStatus, actcAsAccepted = false): Status => {
  switch (status) {
    case PaymentReportStatus.RJCT:
      return Status.ERROR;
    case PaymentReportStatus.PART:
      return Status.WARNING;
    case PaymentReportStatus.ACCP:
    case PaymentReportStatus.ACSP:
    case PaymentReportStatus.ACWC:
    case PaymentReportStatus.ACxx:
      return Status.SUCCESS;
    case PaymentReportStatus.ACSC:
      return Status.SUCCESS_COMPLETE;
    case PaymentReportStatus.ACTC:
      return actcAsAccepted ? Status.SUCCESS : Status.PROGRESS;
    case PaymentReportStatus.ACPT:
    case PaymentReportStatus.PDNG:
    case PaymentReportStatus.RCVD:
    case PaymentReportStatus.PROG:
      return Status.PROGRESS;
    default:
      return null;
  }
};

export const uploadStatusFormat = (status: UploadStatus): Status => (status ? Status[status] : undefined);

export interface Warning {
  message: string;
  detail?: string;
  occuredAt: string;
}

export interface Error {
  message: string;
  detail: string;
}

interface CreatedBy {
  fullName: string;
  username: string;
  uniqueId?: string;
}

export class CreditTransferOrder {
  public uniqueId: string;
  public signatureStatus?: SignatureStatus;
  public signatureDetails?: string;
  public orderId: string;
  public controlSum: string;
  public actualControlSum: number;
  public numberOfTransactions: string;
  public actualNumberOfTransactions: number;
  public createdOn?: string;
  public created: string;
  public createdBy: CreatedBy;
  public deletedAt?: string;
  public deletedBy?: string;
  public warnings: Warning[];
  public ftsStatus?: FtsStatus;
  public dbStatus?: Status;
  public paymentReportStatus?: PaymentReportStatus;
  public uploadStatus?: UploadStatus;
  public sourceType?: SourceType;
  public payment?: Payment;
  public signedByUser: boolean;
  public comments?: string;
  public signableByUser?: boolean;
  public clientFile?: { filename?: string; fileType?: string; fileSize?: number };
  public status?: ClientFileStatus;
  public secondFactorRequiredForSignoff?: boolean;
  public errors?: Error[];
  public deleted?: boolean;
  public transactionGroups?: TransactionGroup[];
  public salaryPayment?: boolean;
  public orderType: string;
  public version: number;

  constructor(json: JsonObject) {
    this.signatureStatus = json.signatureStatus as SignatureStatus;
    this.uniqueId = json.uniqueId;
    this.orderId = json.orderId;
    this.controlSum = json.controlSum;
    this.actualNumberOfTransactions = json.actualNumberOfTransactions;
    this.createdOn = json.created ? formatDate(json.created, 'DD.MM.YYYY HH:mm:ss') : null;
    this.created = json.created;
    this.createdBy = json.createdBy.fullName;
    this.deletedAt = json.deletedAt ? formatDate(json.deletedAt, 'DD.MM.YYYY HH:mm:ss') : null;
    this.deletedBy = json.deletedBy;
    this.signatureDetails = json.signatures
      ? json.signatures.map(
          (signature: JsonObject) =>
            `${formatDate(signature.timestamp, 'DD.MM.YYYY HH:mm:ss')}, ${signature.signer.username}`
        )
      : '-';
    this.warnings = json.warnings;
    this.status = json.status as ClientFileStatus;
    this.ftsStatus = json.ftsStatus as FtsStatus;
    this.dbStatus = dbStatusFormat(json.paymentReportStatus);
    this.uploadStatus = json.uploadStatus as UploadStatus;
    this.paymentReportStatus = json.paymentReportStatus as PaymentReportStatus;
    this.sourceType = SourceType[json.sourceType as SourceType];
    this.payment = json.salaryPayment ? Payment.SALARY : Payment.NORMAL;
    this.signedByUser = json.signedByUser;
    this.sourceType = sourceTypeFormat(json.sourceType);
    this.comments = json.comments;
    this.clientFile = json.clientFile;
    this.errors = json.errors;
    this.deleted = json.deleted;
    this.orderType = json.orderType;
    this.version = json.version;
  }
}
